import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { handleLogout } from "../utils/auth";
import LogoutModal from "./LogoutModal";
import SearchBar from "./SearchBar";
import NotificationBell from "./NotificationBell";
import Logo from "./Logo";
import useScrollFade from "../hooks/useScrollFade";
import { UserRoles } from "../utils/types";
import { getUserRole } from "../utils/roleHelpers";

type NavbarProps = {
  user: any | null;
  userRoles: UserRoles;
};

const Navbar: React.FC<NavbarProps> = ({ user, userRoles }) => {
  const [showModal, setShowModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isNavbarFaded = useScrollFade(400);
  const menuRef = useRef<HTMLUListElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const logoutHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    handleLogout(
      () => setShowModal(true),
      (error) => console.error("Error logging out:", error)
    );
  };

  const canAddPost = () => {
    if (!user || !user.uid) return false;
    const role = getUserRole(userRoles[user.uid]);
    return role === "admin" || role === "writer";
  };

  const isAdmin = () => {
    if (!user || !user.uid) return false;
    return getUserRole(userRoles[user.uid]) === "admin";
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    if (isMenuOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      // Calculate scrollbar width to prevent content shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const isDesktop = window.innerWidth > 960;

      if (isDesktop) {
        // On desktop: prevent scrolling but keep scrollbar visible
        // Prevent scroll events but keep overflow-y: scroll for visible scrollbar
        document.body.style.overflowY = 'scroll';
        document.body.style.height = '100vh';
        // Store scroll position for restoration
        document.body.setAttribute('data-scroll-y', scrollY.toString());
        // Prevent actual scrolling with event listeners
        window.addEventListener('scroll', preventScroll, { passive: false });
        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventScroll, { passive: false });
        // Lock scroll position
        window.scrollTo(0, scrollY);
      } else {
        // On mobile: use fixed positioning (hides scrollbar)
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      // Restore scrolling
      const isDesktop = window.innerWidth > 960;

      if (isDesktop) {
        const scrollY = document.body.getAttribute('data-scroll-y');
        document.body.style.overflowY = '';
        document.body.style.height = '';
        document.body.removeAttribute('data-scroll-y');
        // Remove event listeners
        window.removeEventListener('scroll', preventScroll);
        window.removeEventListener('wheel', preventScroll);
        window.removeEventListener('touchmove', preventScroll);
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY));
        }
      } else {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.paddingRight = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
      }
    }

    return () => {
      // Cleanup: restore scrolling if component unmounts with menu open
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      document.body.style.overflowY = '';
      document.body.style.height = '';
      document.body.removeAttribute('data-scroll-y');
      window.removeEventListener('scroll', preventScroll);
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
    };
  }, [isMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.hamburger-menu')) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <>
      <header
        className={`header-container animate__faster${!isNavbarFaded
          ? " animate__animated animate__fadeInDown"
          : " animate__animated animate__fadeOutUp"
          }`}
      >
        <nav className="nav-wrapper">
          <div className="nav-brand">
            <button
              ref={hamburgerRef}
              className="hamburger-menu"
              onClick={toggleMenu}
              aria-label="Toggle navigation menu"
              {...(isMenuOpen ? { "aria-expanded": "true" as const } : {})}
            >
              <span className={`hamburger-icon ${isMenuOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
            <Link to="/" className="site-logo-link">
              <Logo />
            </Link>
          </div>
          <ul ref={menuRef} className={`nav-container ${isMenuOpen ? "menu-open" : ""}`}>
            <li className="nav-link">
              <NavLink to="/" onClick={closeMenu} end>
                <span className="material-symbols-outlined notranslate">article</span>
                <span className="link-text">Blog</span>
              </NavLink>
            </li>
            <li className="nav-link">
              <NavLink to="/projects" onClick={closeMenu}>
                <span className="material-symbols-outlined notranslate">
                  apps
                </span>
                <span className="link-text">Projects</span>
              </NavLink>
            </li>
            <li className="nav-link">
              <NavLink to="/about" onClick={closeMenu}>
                <span className="material-symbols-outlined notranslate">
                  info
                </span>
                <span className="link-text">About</span>
              </NavLink>
            </li>
            {user && (
              <>
                <li className="nav-link">
                  <NavLink to="/saved" onClick={closeMenu}>
                    <span className="material-symbols-outlined notranslate">
                      bookmark_added
                    </span>
                    <span className="link-text">Saved</span>
                  </NavLink>
                </li>
                <li className="nav-link">
                  <NavLink to={`/profile/${user.uid}`} onClick={closeMenu}>
                    <span className="material-symbols-outlined notranslate">
                      person
                    </span>
                    <span className="link-text">Profile</span>
                  </NavLink>
                </li>
                {canAddPost() && (
                  <li className="nav-link">
                    <NavLink to="/add-post" onClick={closeMenu}>
                      <span className="material-symbols-outlined notranslate">
                        post_add
                      </span>
                      <span className="link-text">Publish</span>
                    </NavLink>
                  </li>
                )}
                {isAdmin() && (
                  <li className="nav-link">
                    <NavLink to="/admin" onClick={closeMenu}>
                      <span className="material-symbols-outlined notranslate">
                        admin_panel_settings
                      </span>
                      <span className="link-text">Admin</span>
                    </NavLink>
                  </li>
                )}
              </>
            )}
            <li className="nav-link">
              {user ? (
                <Link to="" onClick={(e) => { closeMenu(); logoutHandler(e); }}>
                  <span className="material-symbols-outlined notranslate">logout</span>
                  <span className="link-text">Logout</span>
                </Link>
              ) : (
                <NavLink to="/login" onClick={closeMenu}>
                  <span className="material-symbols-outlined notranslate">login</span>
                  <span className="link-text">Login</span>
                </NavLink>
              )}
            </li>
          </ul>
          <div className="nav-actions">
            {user && <NotificationBell />}
            <SearchBar />
          </div>
        </nav>
      </header>
      {showModal && <LogoutModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default Navbar;
